import { useApi } from "@/hooks/use-api";
import { clarityService } from "@/lib/clarity";
import {
  createContext,
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { AuthContextProps, AuthProviderProps } from "./auth.types";
import { useNavigate } from "react-router-dom";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { identifyDatadogUser } from "@/config/datadog-rum";

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export const AuthProvider: FunctionComponent<AuthProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const { data: userInfo, error } = useApi({
    path: "/v0/users/me?fl=profile,plan",
    method: "get",
  });

  useEffect(() => {
    if (error) {
      navigate(DEFAULT_ROUTES.SIGNIN);
    }
  }, [error, navigate]);

  const userClaritydentify = useCallback(() => {
    if (userInfo?.user?.id) {
      const {
        user: { id },
        profile: { email },
      } = userInfo;
      clarityService.identifyClarity({
        userId: String(id),
        email: email,
      });
    }
  }, [userInfo?.user?.id]);

  useEffect(() => {
    userClaritydentify();
  }, [userClaritydentify]);

  const datadogIdentify = useCallback(() => {
    if (userInfo?.user?.id) {
      // id only — no name/email, to avoid exporting PII to Datadog (LGPD).
      identifyDatadogUser({ id: userInfo.user.id.toString() });
    }
  }, [userInfo?.user?.id]);

  useEffect(() => {
    datadogIdentify();
  }, [datadogIdentify]);

  return (
    <AuthContext.Provider value={{ userInfo }}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
